import { useState, useEffect, useRef } from 'react';

export default function StickyHeader() {
  const [activeSection, setActiveSection] = useState('hero');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Use IntersectionObserver for better performance with scroll snapping
    const observerOptions = {
      root: null,
      rootMargin: '-56px 0px -50% 0px', // Account for header height and require 50% visibility
      threshold: 0
    };

    observerRef.current = new IntersectionObserver((entries) => {
      // Find the section that's most visible
      let mostVisibleSection = '';
      let maxRatio = 0;

      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          mostVisibleSection = entry.target.id;
        }
      });

      if (mostVisibleSection) {
        setActiveSection(mostVisibleSection);
      }
    }, observerOptions);

    // Observe all sections
    const sections = ['hero', 'about', 'art-therapy', 'testimonials', 'services-pricing', 'education', 'contact'];
    sections.forEach(sectionId => {
      const element = document.getElementById(sectionId);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Use native scrollIntoView for better snap behavior
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const scrollToBooking = () => {
    scrollToSection('services-pricing');
  };

  return (
    <>
      {/* Sticky Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1c1208]/90 backdrop-blur-md shadow-lg border-b border-[#3a2a1e]/50">
        <div className="px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Left: Brand */}
            <div className="flex flex-col">
              <h1 className="text-lg font-bold text-white leading-tight" style={{ fontFamily: "'Zain', sans-serif" }}>Yuliya Yuvati</h1>
              <p className="text-xs text-gray-300 font-light leading-tight">Art Therapist & Addiction Counselor</p>
            </div>

            {/* Right: Desktop Menu */}
            <nav className="hidden lg:flex items-center space-x-6" role="navigation" aria-label="Main">
              <button
                onClick={() => scrollToSection('about')}
                className={`relative px-2 py-1.5 text-sm font-medium transition-all duration-200 hover:text-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-[#1c1208] rounded-xl ${
                  activeSection === 'about'
                    ? 'text-orange-400'
                    : 'text-gray-300'
                }`}
                aria-current={activeSection === 'about' ? 'page' : undefined}
              >
                About
                {activeSection === 'about' && (
                  <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-orange-400 rounded-full"></span>
                )}
              </button>

              <button
                onClick={() => scrollToSection('art-therapy')}
                className={`relative px-2 py-1.5 text-sm font-medium transition-all duration-200 hover:text-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-[#1c1208] rounded-xl ${
                  activeSection === 'art-therapy'
                    ? 'text-orange-400'
                    : 'text-gray-300'
                }`}
                aria-current={activeSection === 'art-therapy' ? 'page' : undefined}
              >
                Art Therapy
                {activeSection === 'art-therapy' && (
                  <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-orange-400 rounded-full"></span>
                )}
              </button>

              <button
                onClick={() => scrollToSection('testimonials')}
                className={`relative px-2 py-1.5 text-sm font-medium transition-all duration-200 hover:text-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-[#1c1208] rounded-xl ${
                  activeSection === 'testimonials'
                    ? 'text-orange-400'
                    : 'text-gray-300'
                }`}
                aria-current={activeSection === 'testimonials' ? 'page' : undefined}
              >
                Testimonials
                {activeSection === 'testimonials' && (
                  <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-orange-400 rounded-full"></span>
                )}
              </button>

              <button
                onClick={() => scrollToSection('services-pricing')}
                className={`relative px-2 py-1.5 text-sm font-medium transition-all duration-200 hover:text-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-[#1c1208] rounded-xl ${
                  activeSection === 'services-pricing'
                    ? 'text-orange-400'
                    : 'text-gray-300'
                }`}
                aria-current={activeSection === 'services-pricing' ? 'page' : undefined}
              >
                Services & Pricing
                {activeSection === 'services-pricing' && (
                  <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-orange-400 rounded-full"></span>
                )}
              </button>

              <button
                onClick={() => scrollToSection('education')}
                className={`relative px-2 py-1.5 text-sm font-medium transition-all duration-200 hover:text-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-[#1c1208] rounded-xl ${
                  activeSection === 'education'
                    ? 'text-orange-400'
                    : 'text-gray-300'
                }`}
                aria-current={activeSection === 'education' ? 'page' : undefined}
              >
                Education & Certification
                {activeSection === 'education' && (
                  <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-orange-400 rounded-full"></span>
                )}
              </button>

              <button
                onClick={() => scrollToSection('contact')}
                className={`relative px-2 py-1.5 text-sm font-medium transition-all duration-200 hover:text-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-[#1c1208] rounded-xl ${
                  activeSection === 'contact'
                    ? 'text-orange-400'
                    : 'text-gray-300'
                }`}
                aria-current={activeSection === 'contact' ? 'page' : undefined}
              >
                Contact
                {activeSection === 'contact' && (
                  <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-orange-400 rounded-full"></span>
                )}
              </button>
              
              {/* Book Session Button */}
              <button
                onClick={scrollToBooking}
                className="ml-3 px-4 py-1.5 bg-orange-500 text-white text-sm font-medium rounded-xl hover:bg-orange-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-[#1c1208] whitespace-nowrap"
              >
                Book Session
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-1.5 text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-[#1c1208] rounded-md"
              aria-label="Toggle menu"
            >
              <div className="w-5 h-5 flex flex-col justify-center items-center">
                <span className={`block w-4 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-0.5' : '-translate-y-0.5'}`}></span>
                <span className={`block w-4 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`block w-4 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-0.5' : 'translate-y-0.5'}`}></span>
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40 bg-[#1c1208]/50 backdrop-blur-sm lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
          
          {/* Drawer */}
          <div className="fixed top-0 right-0 z-50 w-80 h-full bg-[#1c1208]/95 backdrop-blur-md shadow-2xl lg:hidden transform transition-transform duration-300">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-[#3a2a1e]">
                <div>
                  <h2 className="text-lg font-bold text-white">Menu</h2>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-400 rounded-md"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {/* Menu Items */}
              <nav className="flex-1 px-6 py-8 space-y-4" role="navigation">
                <button
                  onClick={() => scrollToSection('about')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                    activeSection === 'about'
                      ? 'bg-orange-500/20 text-orange-400 border-l-4 border-orange-400'
                      : 'text-gray-300 hover:text-white hover:bg-[#2a1a0e]/50'
                  }`}
                  aria-current={activeSection === 'about' ? 'page' : undefined}
                >
                  About
                </button>

                <button
                  onClick={() => scrollToSection('art-therapy')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                    activeSection === 'art-therapy'
                      ? 'bg-orange-500/20 text-orange-400 border-l-4 border-orange-400'
                      : 'text-gray-300 hover:text-white hover:bg-[#2a1a0e]/50'
                  }`}
                  aria-current={activeSection === 'art-therapy' ? 'page' : undefined}
                >
                  Art Therapy
                </button>

                <button
                  onClick={() => scrollToSection('testimonials')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                    activeSection === 'testimonials'
                      ? 'bg-orange-500/20 text-orange-400 border-l-4 border-orange-400'
                      : 'text-gray-300 hover:text-white hover:bg-[#2a1a0e]/50'
                  }`}
                  aria-current={activeSection === 'testimonials' ? 'page' : undefined}
                >
                  Testimonials
                </button>

                <button
                  onClick={() => scrollToSection('services-pricing')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                    activeSection === 'services-pricing'
                      ? 'bg-orange-500/20 text-orange-400 border-l-4 border-orange-400'
                      : 'text-gray-300 hover:text-white hover:bg-[#2a1a0e]/50'
                  }`}
                  aria-current={activeSection === 'services-pricing' ? 'page' : undefined}
                >
                  Services & Pricing
                </button>

                <button
                  onClick={() => scrollToSection('education')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                    activeSection === 'education'
                      ? 'bg-orange-500/20 text-orange-400 border-l-4 border-orange-400'
                      : 'text-gray-300 hover:text-white hover:bg-[#2a1a0e]/50'
                  }`}
                  aria-current={activeSection === 'education' ? 'page' : undefined}
                >
                  Education & Certification
                </button>

                <button
                  onClick={() => scrollToSection('contact')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                    activeSection === 'contact'
                      ? 'bg-orange-500/20 text-orange-400 border-l-4 border-orange-400'
                      : 'text-gray-300 hover:text-white hover:bg-[#2a1a0e]/50'
                  }`}
                  aria-current={activeSection === 'contact' ? 'page' : undefined}
                >
                  Contact
                </button>
              </nav>

              {/* Book Session Button */}
              <div className="p-6 border-t border-[#3a2a1e]">
                <button
                  onClick={scrollToBooking}
                  className="w-full px-6 py-3 bg-orange-500 text-white text-base font-medium rounded-xl hover:bg-orange-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 whitespace-nowrap"
                >
                  Book Session
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Spacer to prevent content from hiding under fixed header */}
      <div className="h-14"></div>
    </>
  );
}
